/**
 * Inline list of child-task rows rendered beneath an expanded
 * <DelegationRollupChip>. Clicking a row opens the child's task detail.
 *
 * Spec: docs/specs/2026-04-16-session-state-audit.md §3.5.
 */

import { useCallback } from "react";
import { cn } from "@/lib/cn";
import { TaskStatusGlyph } from "@/components/ui";
import type { TaskChildSummary } from "@/api/tasks";

interface DelegationChildListProps {
  children: TaskChildSummary[];
  onChildClick: (taskId: string) => void;
  className?: string;
}

export function DelegationChildList({
  children,
  onChildClick,
  className,
}: DelegationChildListProps) {
  if (children.length === 0) return null;

  return (
    <ul
      className={cn("mt-1 flex flex-col gap-0.5", className)}
      data-testid="delegation-child-list"
    >
      {children.map((child) => (
        <ChildRow key={child.id} child={child} onClick={onChildClick} />
      ))}
    </ul>
  );
}

function ChildRow({
  child,
  onClick,
}: {
  child: TaskChildSummary;
  onClick: (id: string) => void;
}) {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLLIElement>) => {
      e.stopPropagation();
      onClick(child.id);
    },
    [child.id, onClick],
  );

  return (
    <li
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onClick(child.id);
        }
      }}
      className={cn(
        "flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5",
        "text-appkit-mini text-secondary",
      )}
      data-testid="delegation-child-row"
      data-child-id={child.id}
    >
      <TaskStatusGlyph status={child.status} size="sm" />
      {child.ticket_display && (
        <span className="shrink-0 font-mono text-muted">
          {child.ticket_display}
        </span>
      )}
      <span className="truncate">{child.title}</span>
      {child.agent_name && (
        <span className="ml-auto shrink-0 truncate text-muted">
          {child.agent_name}
        </span>
      )}
    </li>
  );
}
